import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { PlanCard } from './components';
import { COLORS } from './tokens';
import type { PlanNote } from './planRecordApi';
import { displayYear, formatPlanDate } from './planRecordView';

export const NOTE_PLACEHOLDER = "What should next year's teacher know?";

const META_SX = { fontSize: 12, fontWeight: 600, lineHeight: '16px', color: COLORS.muted } as const;

function noteYear(note: PlanNote): string {
  const year = displayYear(note.schoolYearWritten);
  const standing = note.standingWritten;
  if (standing && standing !== 'active') return standing === 'monitoring' ? `${year}, monitoring` : year;
  return `${year} plan`;
}

export function NotesCard({
  firstName,
  notes,
  onAdd,
  openRequest = 0,
  canAdd = true,
  subtitle,
}: {
  firstName: string;
  notes: PlanNote[];
  onAdd: (text: string) => Promise<void>;
  openRequest?: number;
  canAdd?: boolean;
  subtitle?: string;
}) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [seenRequest, setSeenRequest] = useState(openRequest);
  if (openRequest !== seenRequest) {
    setSeenRequest(openRequest);
    if (openRequest > 0) setAdding(true);
  }

  const save = async () => {
    const trimmed = text.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    setFailed(false);
    try {
      await onAdd(trimmed);
      setText('');
      setAdding(false);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PlanCard
      title="Notes"
      subtitle={subtitle ?? `Notes stay with ${firstName} from year to year, even after a plan is finalized.`}
      right={
        adding || !canAdd ? undefined : (
          <Button
            variant="outlined"
            onClick={() => setAdding(true)}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: COLORS.navy, borderColor: COLORS.hairline, flexShrink: 0 }}
          >
            Add note
          </Button>
        )
      }
      sx={{ gap: 2 }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {adding && (
          <Box sx={{ border: `1px solid ${COLORS.divider}`, borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
              component="textarea"
              value={text}
              placeholder={NOTE_PLACEHOLDER}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
              sx={{
                width: '100%',
                minHeight: 72,
                resize: 'vertical',
                border: `1px solid ${COLORS.divider}`,
                borderRadius: 2,
                p: 1.5,
                fontFamily: 'inherit',
                fontSize: 16,
                lineHeight: '24px',
                color: COLORS.ink,
                boxSizing: 'border-box',
                '&:focus': { outline: `2px solid ${COLORS.navy}`, outlineOffset: -1 },
              }}
            />
            {failed && (
              <Typography sx={{ fontSize: 13, color: '#B20101' }}>Couldn’t save the note. Try again.</Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={() => { setAdding(false); setText(''); }} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: COLORS.ink }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={saving}
                onClick={() => { void save(); }}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, bgcolor: COLORS.navy, '&:hover': { bgcolor: '#23236F' } }}
              >
                Save note
              </Button>
            </Box>
          </Box>
        )}
        {notes.map((note) => (
          <Box key={note.id} sx={{ border: `1px solid ${COLORS.divider}`, borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography data-testid="note-meta" sx={META_SX}>
              {`${formatPlanDate(note.createdAt)} · ${note.author.name}, ${note.role} · ${noteYear(note)}`}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: COLORS.ink, whiteSpace: 'pre-wrap' }}>{note.text}</Typography>
          </Box>
        ))}
      </Box>
    </PlanCard>
  );
}
